"use client";

import type { ReactNode } from "react";
import { Panchang } from "@/components/panchang";
import DailyReadingWhatsAppLink from "@/components/kundali/DailyReadingWhatsAppLink";
import RatingPrompt from "@/components/RatingPrompt";
import { DEFAULT_PANCHANG_CITY, PanchangCityProvider } from "@/contexts/PanchangCity";
import type { PanchangResponse } from "@/lib/api/panchang";
import type { PanchangCalculationDetail } from "@/components/panchang/Panchang.sections";
import type { CityData } from "@/lib/cities-data.types";
import type { TodayObservance } from "@/lib/panchang-today-observance";

export default function PanchangClient({
  latitude = 28.6139,
  longitude = 77.209,
  defaultLanguage = "en",
  heading,
  eyebrow,
  headingLevel = "h1",
  initialDate,
  initialDisplayDate,
  initialResult,
  initialCity = DEFAULT_PANCHANG_CITY,
  allowCityChange = true,
  calculationDetail = "full",
  resultOffer,
  crossLinks,
  citySelector,
  todayObservance = null,
}: {
  latitude?: number;
  longitude?: number;
  defaultLanguage?: "en" | "hi";
  initialDate?: string;
  heading?: string;
  eyebrow?: string;
  headingLevel?: "h1" | "h2";
  initialDisplayDate?: string;
  initialResult?: PanchangResponse | null;
  initialCity?: CityData;
  allowCityChange?: boolean;
  calculationDetail?: PanchangCalculationDetail;
  resultOffer: ReactNode;
  crossLinks: ReactNode;
  /** URL-addressed selector; when present the stored-city restore is off. */
  citySelector?: ReactNode;
  /** Today's festival/vrat chip for the hub header (server-computed). */
  todayObservance?: TodayObservance | null;
}) {
  return (
    <>
      <div className="intent-interface-timing-first min-h-[calc(100vh-130px)]">
        <PanchangCityProvider
          initialCity={{
            ...initialCity,
            latitude,
            longitude,
          }}
          restoreStoredCity={allowCityChange && !citySelector}
        >
          <Panchang
            language={defaultLanguage}
            initialDate={initialDate}
            heading={heading}
            eyebrow={eyebrow}
            headingLevel={headingLevel}
            initialDisplayDate={initialDisplayDate}
            initialResult={initialResult}
            initialCity={initialCity}
            allowCityChange={allowCityChange}
            calculationDetail={calculationDetail}
            resultOffer={resultOffer}
            crossLinks={crossLinks}
            citySelector={citySelector}
            todayObservance={todayObservance}
          />
        </PanchangCityProvider>
      </div>
      <div
        style={{ maxWidth: 640, margin: "0 auto", padding: "0 16px 24px" }}
        className="flex flex-col items-center"
      >
        <RatingPrompt
          source={"tool_result" as never}
          mode="thumbs+text"
          prompt="Was today's Panchang accurate?"
          context={{
            surface: "panchang_result",
            path: typeof window !== "undefined" ? window.location.pathname : null,
          }}
        />
        <DailyReadingWhatsAppLink lang={defaultLanguage} />
      </div>
    </>
  );
}
